import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
// import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Briefcase,
  Coins,
  FileText,
  LayoutDashboard,
  Shield,
  Users,
} from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  categorizePermissions,
  getPermissionAction,
  type Permission,
} from '../types'

interface PermissionsSelectorProps {
  allPermissions: Permission[]
  selectedPermissions: string[]
  onChange: (permissions: string[]) => void
  disabled?: boolean
}

const categoryIcons: Record<string, React.ReactNode> = {
  USER: <Users className='h-4 w-4' />,
  ROLE: <Shield className='h-4 w-4' />,
  ENGINE: <Briefcase className='h-4 w-4' />,
  INVESTOR: <Coins className='h-4 w-4' />,
  DOCUMENT: <FileText className='h-4 w-4' />,
  DASHBOARD: <LayoutDashboard className='h-4 w-4' />,
}

const actionColors: Record<string, string> = {
  READ: 'hsl(var(--chart-1))',
  CREATE: 'hsl(var(--chart-2))',
  UPDATE: 'hsl(var(--chart-3))',
  DELETE: 'hsl(var(--chart-4))',
}

export function PermissionsSelector({
  allPermissions,
  selectedPermissions,
  onChange,
  disabled = false,
}: PermissionsSelectorProps) {
  const { t } = useTranslation()
  const categories = categorizePermissions(allPermissions)

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onChange(allPermissions.map(p => p.full_codename))
    } else {
      onChange([])
    }
  }

  const handleCategorySelectAll = (
    categoryPermissions: Permission[],
    checked: boolean
  ) => {
    const categoryIds = categoryPermissions.map(p => p.full_codename);
    if (checked) {
      // Add all permissions from this category
      const newPermissions = [
        ...selectedPermissions,
        ...categoryIds.filter((p) => !selectedPermissions.includes(p)),
      ]
      onChange(newPermissions)
    } else {
      // Remove all permissions from this category
      const newPermissions = selectedPermissions.filter(
        (p) => !categoryIds.includes(p)
      )
      onChange(newPermissions)
    }
  }

  const handlePermissionToggle = (permission: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedPermissions, permission])
    } else {
      onChange(selectedPermissions.filter((p) => p !== permission))
    }
  }

  const allSelected = selectedPermissions.length === allPermissions.length
  const someSelected =
    selectedPermissions.length > 0 &&
    selectedPermissions.length < allPermissions.length

  return (
    <div className='space-y-4' dir={t('dir') as 'ltr' | 'rtl'}>
      {/* Select All */}
      <div className='flex items-center gap-2 border-b pb-3'>
        <Checkbox
          id='select-all'
          checked={allSelected}
          onCheckedChange={handleSelectAll}
          disabled={disabled}
          className='data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
          aria-label='Select all permissions'
        />
        <Label
          htmlFor='select-all'
          className='cursor-pointer text-sm font-semibold'
        >
          {allSelected
            ? t('roles.deselectAll', 'Deselect All')
            : t('roles.selectAll', 'Select All')}
          {someSelected && (
            <span className='text-muted-foreground ml-2'>
              ({selectedPermissions.length}/{allPermissions.length})
            </span>
          )}
        </Label>
      </div>

      {/* Permissions by Category */}
      {/* Permissions by Category */}
      <div className='pe-4'>
        <div className='space-y-3'>
          {categories.map((category) => {
            const categoryPermissions = category.permissions
            const selectedInCategory = categoryPermissions.filter((p) =>
              selectedPermissions.includes(p.full_codename)
            )
            const allCategorySelected =
              selectedInCategory.length === categoryPermissions.length

            return (
              <Collapsible key={category.name} defaultOpen>
                <div className='bg-card rounded-lg border'>
                  <div className='hover:bg-accent flex w-full items-center justify-between p-3 transition-colors'>
                    <CollapsibleTrigger className='flex flex-1 items-center gap-2'>
                      {categoryIcons[category.name] || (
                        <Shield className='h-4 w-4' />
                      )}
                      <span className='text-sm font-semibold'>
                        {t(
                          `roles.categories.${category.name.toLowerCase()}`,
                          category.name
                        )}
                      </span>
                      <Badge variant='secondary' className='text-xs'>
                        {selectedInCategory.length}/{categoryPermissions.length}
                      </Badge>
                    </CollapsibleTrigger>
                    <Checkbox
                      checked={allCategorySelected}
                      onCheckedChange={(checked) =>
                        handleCategorySelectAll(
                          categoryPermissions,
                          checked as boolean
                        )
                      }
                      disabled={disabled}
                      onClick={(e) => e.stopPropagation()}
                      className='data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
                      aria-label={`Select all ${category.name} permissions`}
                    />
                  </div>

                  <CollapsibleContent className='px-3 pt-0 pb-3'>
                    <div className='mt-2 space-y-2'>
                      {categoryPermissions.map((permission) => {
                        const action = getPermissionAction(permission)
                        const actionColor =
                          actionColors[action] || 'hsl(var(--foreground))'
                        const isChecked =
                          selectedPermissions.includes(permission.full_codename)

                        return (
                          <div
                            key={permission.id}
                            className='hover:bg-accent flex items-center gap-2 rounded-md p-2 transition-colors'
                          >
                            <Checkbox
                              id={permission.full_codename}
                              checked={isChecked}
                              onCheckedChange={(checked) =>
                                handlePermissionToggle(
                                  permission.full_codename,
                                  checked as boolean
                                )
                              }
                              disabled={disabled}
                              className='data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
                            />
                            <Label
                              htmlFor={permission.full_codename}
                              className='flex flex-1 cursor-pointer items-center justify-between gap-2 text-sm'
                            >
                              <span>
                                {t(
                                  `roles.permissions.${permission.full_codename}`,
                                  permission.name
                                )}
                              </span>
                              <Badge
                                variant='outline'
                                style={{
                                  borderColor: actionColor,
                                  color: actionColor,
                                }}
                                className='text-xs'
                              >
                                {t(`permission_actions.${action}`, action)}
                              </Badge>
                            </Label>
                          </div>
                        )
                      })}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )
          })}
        </div>
      </div>
    </div>
  )
}
